import { confirmSignUp, resendSignUpCode, signIn, signUp } from 'aws-amplify/auth';
import { useState } from 'react';
import {
  COMPROMISED_PASSWORD_MESSAGE,
  EXISTING_ACCOUNT_COMPROMISED_MESSAGE,
} from '../components/auth-mfa/AuthErrorMessage';
import { classifyAuthError } from './classify-auth-error';

export type SignUpStep = 'signup' | 'confirm' | 'complete';

interface UseSignUpOptions {
  autoSignIn: boolean;
  onDone: () => void;
}

export function useSignUp({ autoSignIn, onDone }: UseSignUpOptions) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<SignUpStep>('signup');

  /** 未確認ユーザーに確認コードを再送信し、確認ステップへ進める */
  const resendCodeAndConfirm = async () => {
    try {
      await resendSignUpCode({ username: email });
      setStep('confirm');
    } catch (resendErr) {
      const message =
        resendErr instanceof Error ? resendErr.message : '確認コードの再送信に失敗しました';
      setError(message);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setLoading(true);

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
          autoSignIn,
        },
      });

      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setStep('confirm');
      } else if (result.nextStep.signUpStep === 'DONE') {
        onDone();
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'UsernameExistsException') {
        try {
          // signIn で確認状態を判定（未確認状態は preventUserExistenceErrors でもマスクされない）
          // サインイン画面と同じくパスワード直接送信で行う（SRP は無効化するため）
          const signInResult = await signIn({
            username: email,
            password,
            options: { authFlowType: 'USER_PASSWORD_AUTH' },
          });
          // Amplify v6 の signIn は未確認ユーザーでも例外を投げず、
          // nextStep.signInStep === 'CONFIRM_SIGN_UP' として解決される
          if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
            await resendCodeAndConfirm();
            return;
          }
          // 登録済みのパスワードが漏洩パスワードとして阻止された。サインイン画面へ送っても
          // そこで再び阻止されるため、ここでパスワードの再設定を促す
          if (signInResult.nextStep.signInStep === 'RESET_PASSWORD') {
            setError(EXISTING_ACCOUNT_COMPROMISED_MESSAGE);
            return;
          }
          // それ以外（DONE・MFA のコード入力や登録）は確認済みユーザー（パスワード一致）として扱う
          onDone();
          return;
        } catch (signInErr) {
          if (signInErr instanceof Error && signInErr.name === 'UserNotConfirmedException') {
            // 未確認ユーザーが例外として返る経路のフォールバック
            await resendCodeAndConfirm();
            return;
          }
          // 確認済みユーザー（パスワード不一致等）
          setError('このメールアドレスは既に登録されています');
          return;
        }
      }
      if (classifyAuthError(err).kind === 'compromisedCredentials') {
        setError(COMPROMISED_PASSWORD_MESSAGE);
        return;
      }
      const message = err instanceof Error ? err.message : 'サインアップに失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode,
      });

      if (result.isSignUpComplete) {
        setStep('complete');
      } else {
        setError(`追加の確認が必要です: ${result.nextStep.signUpStep}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '確認に失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    confirmationCode,
    setConfirmationCode,
    error,
    loading,
    step,
    handleSignUp,
    handleConfirm,
  };
}
